import * as Sentry from '@sentry/react';

/**
 * Initialise Sentry if a DSN is configured. Safe to call multiple times
 * (Sentry.init is idempotent in practice). We don't throw when the DSN
 * is missing because local dev / CI builds often don't need reporting.
 */
export function initSentry(appName: 'customer' | 'admin' | 'partner') {
  const dsn = (import.meta as any).env?.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: (import.meta as any).env?.MODE ?? 'production',
    // Capture IP and other default PII so we can dedupe unique users
    // in Sentry. We're already covered under the customer privacy
    // policy; disable this if you ever need stricter GDPR posture.
    sendDefaultPii: true,
    // Tag every event with the app name so the three feeds don't mix
    // in Sentry's UI even if they share a single DSN during bootstrapping.
    initialScope: { tags: { app: appName } },
    // Record breadcrumbs for network calls and console logs so a crash
    // report includes the last ~5 seconds of user actions.
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 0.1,        // 10% of navigations sampled for perf traces
    replaysSessionSampleRate: 0.0, // no session replays by default (cost control)
    replaysOnErrorSampleRate: 1.0, // but always capture a replay when an error fires
    // Filter out the "message channel closed" noise caused by browser
    // extensions (not our code), plus a couple of common injected-script
    // errors that pollute the dashboard.
    ignoreErrors: [
      /listener indicated an asynchronous response/i,
      /ResizeObserver loop/i,
      /Non-Error promise rejection captured/i,
    ],
  });
}

export { Sentry };
